import { useState, useEffect } from 'react';
import { useHTTP } from 'hooks/useRequest';
import useProductShowStore from 'stores/product_show_store/useProductShowStore';
import { addToCartEventsGA4 } from 'data_layer/ga_add_to_basket_events';
import {
  setBasketHeaderLoading,
  createBasketJSON,
  displaySuccessfulUpdate,
  addToBasketTracking,
} from 'lib/addToBasketHelpers';

export const useBasketAction = ({
  stepId,
  addToBasketUrl,
  productPrice,
  isReserveCollect,
  title,
  brand,
  category,
  stockCode,
  isSimilarStock,
  onBackOrderRequired,
  partCode,
  type,
  stockLevel,
  selectedStoreName,
}) => {
  const [canValidateQuantity, setCanValidateQuantity] = useState(false);
  const [isValidQuantity, setIsValidQuantity] = useState(false);

  const {
    quantitySelectorInput,
    setProductBasketQuantity,
    setIsAddingToBasket,
  } = useProductShowStore();

  const addProductToBasket = () => {
    if (isReserveCollect && window.dataLayer) {
      window.dataLayer.push({
        event: 'reserveCollectCTA',
        storeName: selectedStoreName,
      });
    }

    addToCartEventsGA4({
      event: 'add_to_cart_ga4',
      options: {
        page: type === 'sticky' ? 'Sticky Basket' : 'Product Detail',
        products: [
          {
            name: title,
            brand: brand || '',
            part_code: partCode,
            idVariant: stepId,
            id: stepId,
            primary_root_category: category,
            stock_code: stockCode,
            brand_and_description_short: (brand || '') + title,
            isSimilarStock,
          },
        ],
        prices: productPrice?.[stepId]?.price || 0,
      },
    });

    addToBasketTracking({
      price: productPrice?.[stepId]?.price || 0,
      stepId,
      stockCode,
      title,
      brand: brand || '',
      category,
    });

    setIsValidQuantity(true);
    setIsAddingToBasket(isReserveCollect ? 'collection' : 'delivery');
    setBasketHeaderLoading();
  };

  const { data: quantityCalculationResponse } = useHTTP({
    method: 'GET',
    url: canValidateQuantity
      ? `/basket/quantity_calculation?quantity=${quantitySelectorInput}&step_id=${stepId}`
      : null,
  });

  const basketRequestConfig = {
    method: 'PUT',
    url: isValidQuantity ? addToBasketUrl : null,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: createBasketJSON(
      [
        {
          product_id: stepId,
          quantity: quantitySelectorInput,
        },
      ],
      isReserveCollect,
    ),
  };

  const { data: addToBasketResponse } = useHTTP(basketRequestConfig || null);

  const triggerAction = () => {
    setCanValidateQuantity(true);
  };

  const forceAction = () => {
    addProductToBasket();
  };

  useEffect(() => {
    if (quantityCalculationResponse) {
      const { quantity } = quantityCalculationResponse;
      setProductBasketQuantity(quantity);

      if (quantity <= stockLevel) {
        addProductToBasket();
      } else if (onBackOrderRequired) {
        setIsValidQuantity(false);
        onBackOrderRequired();
      }
      setCanValidateQuantity(false);
    }
  }, [quantityCalculationResponse, stockLevel]);

  useEffect(() => {
    if (addToBasketResponse) {
      displaySuccessfulUpdate(addToBasketResponse, () => {
        setIsAddingToBasket(false);
        setIsValidQuantity(false);
        PubSub.publish('FETCH-USER-CREDIT-LIMIT', true);
      });
    }
  }, [addToBasketResponse]);

  return {
    triggerAction,
    forceAction,
    isLoading: canValidateQuantity || isValidQuantity,
  };
};
